import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface MenuViewerProps {
  menuImages: string[];
}

const MenuViewer: React.FC<MenuViewerProps> = ({
  menuImages,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1);

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % menuImages.length);
    setScale(1);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev === 0 ? menuImages.length - 1 : prev - 1));
    setScale(1);
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.3, 3));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.3, 0.5));
  };

  if (menuImages.length === 0) {
    return (
      <div className="bg-gray-900 border-2 border-amber-700/50 shadow-2xl rounded-lg overflow-hidden p-8">
        <div className="text-center">
          <p className="text-gray-300 mb-4">
            Menu images are being prepared. The PDF menu is temporarily unavailable for optimal performance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border-2 border-amber-700/50 shadow-2xl rounded-lg overflow-hidden relative">
      {/* Desktop View */}
      <div className="hidden lg:block">
        <div className="relative h-[90vh] overflow-auto bg-gray-800" style={{ scrollbarWidth: 'thin', scrollbarColor: '#D4A574 #1F2937' }}>
          <div className="flex flex-col items-center py-8 space-y-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            {menuImages.map((image, index) => (
              <div key={index} className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-700/50 to-amber-600/50 rounded-lg blur opacity-50 group-hover:opacity-75 transition duration-300"></div>
                <div className="relative bg-gray-900 p-2 rounded-lg">
                  <img
                    src={image}
                    alt={`Menu page ${index + 1}`}
                    className="w-full h-auto rounded shadow-2xl"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden relative h-[80vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 flex items-center justify-center p-2"
          >
            <div 
              className="relative overflow-auto max-h-full max-w-full"
              style={{ transform: `scale(${scale})` }}
            >
              <div className="relative bg-gray-900 p-1 rounded-lg shadow-2xl border border-amber-700/30">
                <img
                  src={menuImages[currentPage]}
                  alt={`Menu page ${currentPage + 1}`}
                  className="w-full h-[75vh] object-contain rounded"
                />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Mobile Controls */}
        <>
          {/* Navigation Buttons */}
          <button
            onClick={prevPage}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-800/80 border border-amber-700/50 text-amber-100 p-2 rounded-full z-10 hover:bg-gray-700/80 hover:border-amber-600 transition-all duration-200"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextPage}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-800/80 border border-amber-700/50 text-amber-100 p-2 rounded-full z-10 hover:bg-gray-700/80 hover:border-amber-600 transition-all duration-200"
          >
            <ChevronRight size={24} />
          </button>

          {/* Top Controls */}
          <div className="absolute top-2 left-2 flex gap-2 z-10">
            <button
              onClick={zoomIn}
              className="bg-gray-800/80 border border-amber-700/50 text-amber-100 p-2 rounded-full hover:bg-gray-700/80 hover:border-amber-600 transition-all duration-200"
            >
              <ZoomIn size={20} />
            </button>
            <button
              onClick={zoomOut}
              className="bg-gray-800/80 border border-amber-700/50 text-amber-100 p-2 rounded-full hover:bg-gray-700/80 hover:border-amber-600 transition-all duration-200"
            >
              <ZoomOut size={20} />
            </button>
          </div>

          {/* Page Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-gray-800/80 border border-amber-700/50 text-amber-100 px-3 py-1 rounded-full text-sm z-10">
            {currentPage + 1} / {menuImages.length}
          </div>
        </>
      </div>
    </div>
  );
};

export default MenuViewer;